import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiArrowLeft,
  FiRefreshCw,
  FiClock,
  FiUsers,
  FiLayers,
  FiCreditCard,
  FiChevronLeft,
  FiChevronRight,
  FiCopy,
  FiX,
  FiKey,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { bonusPoolApi } from '../services/bonusPoolApi';
import { useAuth } from '../contexts/AuthContext';
import { formatLocalDate, formatLocalDateTime, parseDateInput } from '../utils/formatters';

const formatAmount = (value) => {
  if (typeof value !== 'number') {
    return '-';
  }
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
  return `${formatted}U`;
};

const formatTime = (value) => {
  if (!value) {
    return '-';
  }
  return formatLocalDateTime(value, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }
  return formatLocalDate(value, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
};

const formatCountdown = (targetTime, now) => {
  if (!targetTime) {
    return '-';
  }
  const targetDate = parseDateInput(targetTime);
  if (!targetDate) {
    return '-';
  }
  const target = targetDate.getTime();
  const diff = Math.max(0, target - now);
  if (diff <= 0) {
    return '00:00:00';
  }
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) {
    return `${days}d ${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
    seconds
  ).padStart(2, '0')}`;
};

const sortRounds = (items) => {
  const toTime = (round) => {
    const value = round.startTime || round.endTime || round.drawTime || '';
    const parsed = parseDateInput(value);
    return parsed ? parsed.getTime() : 0;
  };

  return [...items].sort((a, b) => toTime(a) - toTime(b));
};

export default function BonusPoolRoundsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rounds, setRounds] = useState([]);
  const [selectedRoundId, setSelectedRoundId] = useState(null);
  const [ticketMap, setTicketMap] = useState({});
  const [ticketLoading, setTicketLoading] = useState(false);
  const [roundDetail, setRoundDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [claimResult, setClaimResult] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [fireworksActive, setFireworksActive] = useState(false);
  const [exportKeyModalOpen, setExportKeyModalOpen] = useState(false);
  const [exportedPrivateKey, setExportedPrivateKey] = useState(null);
  const [exportKeyLoading, setExportKeyLoading] = useState(false);
  const [exportKeyError, setExportKeyError] = useState('');
  const [privateKeyCopied, setPrivateKeyCopied] = useState(false);
  const roundsScrollerRef = useRef(null);

  const statusLabel = useCallback(
    (status) => {
      if (status === 'ongoing') return t('bonusPool.statusOngoing');
      if (status === 'pending_draw') return t('bonusPool.statusPending');
      if (status === 'ended') return t('bonusPool.statusEnded');
      return status || '-';
    },
    [t]
  );

  const loadRounds = useCallback(async () => {
    setLoading(true);
    setError('');
    const result = await bonusPoolApi.getRounds();
    const responseCode = result.data?.code;
    const isSuccess =
      responseCode === 'SUCCESS' || responseCode === 0 || responseCode === '0';
    if (!result.ok || !result.data || !isSuccess) {
      setError(result.data?.msg || t('common.error'));
      setRounds([]);
      setLoading(false);
      return;
    }
    const items = result.data.data?.rounds || [];
    setRounds(sortRounds(items));
    setLoading(false);
  }, [t]);

  useEffect(() => {
    loadRounds();
  }, [loadRounds]);

  useEffect(() => {
    if (rounds.length === 0) {
      setSelectedRoundId(null);
      return;
    }
    setSelectedRoundId((prev) => {
      if (prev && rounds.some((round) => round.id === prev)) {
        return prev;
      }
      const current =
        rounds.find((round) => round.status === 'ongoing') ||
        rounds.find((round) => round.status === 'pending_draw') ||
        rounds[0];
      return current?.id || null;
    });
  }, [rounds, isAuthenticated, authLoading]);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!isAuthenticated || !selectedRoundId) {
      setTicketLoading(false);
      return;
    }
    if (ticketMap[selectedRoundId]) {
      return;
    }

    let cancelled = false;
    const currentRoundId = selectedRoundId;
    const loadTickets = async () => {
      setTicketLoading(true);
      const result = await bonusPoolApi.getMyTickets(currentRoundId);
      const responseCode = result.data?.code;
      const isSuccess =
        responseCode === 'SUCCESS' || responseCode === 0 || responseCode === '0';
      const tickets =
        result.ok && result.data && isSuccess ? result.data.data?.tickets || [] : [];
      if (!cancelled) {
        setTicketMap((prev) => ({ ...prev, [currentRoundId]: tickets }));
        setTicketLoading(false);
      }
    };

    loadTickets();
    return () => {
      cancelled = true;
    };
  }, [selectedRoundId, isAuthenticated, authLoading, ticketMap]);

  const fetchRoundDetail = useCallback(async (roundId, silent = false) => {
    if (!silent) {
      setDetailLoading(true);
    }
    const result = await bonusPoolApi.getRoundDetail(roundId);
    const responseCode = result.data?.code;
    const isSuccess =
      responseCode === 'SUCCESS' || responseCode === 0 || responseCode === '0';
    if (result.ok && result.data && isSuccess) {
      setRoundDetail(result.data.data);
      if (!silent) {
        setDetailLoading(false);
      }
      return result.data.data;
    }
    if (!silent) {
      setRoundDetail(null);
      setDetailLoading(false);
    }
    return null;
  }, []);

  useEffect(() => {
    if (!selectedRoundId) {
      setRoundDetail(null);
      setClaimResult(null);
      setClaimError('');
      setInviteCopied(false);
      return;
    }

    let cancelled = false;
    const loadDetail = async () => {
      const detail = await fetchRoundDetail(selectedRoundId);
      if (cancelled) {
        return;
      }
      if (!detail) {
        return;
      }
    };

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedRoundId, fetchRoundDetail]);

  useEffect(() => {
    setInviteCopied(false);
  }, [claimResult?.inviteLink]);

  useEffect(() => {
    const hasOngoing = rounds.some((round) => round.status === 'ongoing');
    if (!hasOngoing) {
      return;
    }
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [rounds]);

  useEffect(() => {
    if (!isAuthenticated || !selectedRoundId) {
      return;
    }
    const interval = setInterval(() => {
      fetchRoundDetail(selectedRoundId, true);
    }, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated, selectedRoundId, fetchRoundDetail]);

  const emptyState = useMemo(() => {
    if (loading) return t('common.loading');
    if (error) return error;
    return t('bonusPool.noRounds');
  }, [loading, error, t]);

  const selectedRound = useMemo(
    () => rounds.find((round) => round.id === selectedRoundId) || null,
    [rounds, selectedRoundId]
  );

  const participantCount = useMemo(() => {
    if (roundDetail?.participants != null) {
      return roundDetail.participants;
    }
    if (selectedRound?.participants != null) {
      return selectedRound.participants;
    }
    return selectedRound?.subscribers ?? '-';
  }, [roundDetail, selectedRound]);

  const myPrize = roundDetail?.myPrize || null;
  const prizeKey = useMemo(() => {
    if (!myPrize?.prizeId || !selectedRoundId) {
      return '';
    }
    return `${selectedRoundId}:${myPrize.prizeId}`;
  }, [selectedRoundId, myPrize?.prizeId]);

  useEffect(() => {
    if (!prizeKey) {
      return;
    }
    const seenKey = localStorage.getItem(`bonus_pool_win_seen_${prizeKey}`);
    if (seenKey) {
      return;
    }
    setClaimModalOpen(true);
    setFireworksActive(true);
    localStorage.setItem(`bonus_pool_win_seen_${prizeKey}`, '1');
    const timer = setTimeout(() => setFireworksActive(false), 5000);
    return () => clearTimeout(timer);
  }, [prizeKey]);

  const countdownText = useMemo(() => {
    if (!selectedRound || selectedRound.status !== 'ongoing') {
      return null;
    }
    return formatCountdown(selectedRound.endTime, now);
  }, [selectedRound, now]);

  const ticketSourceLabel = useCallback(
    (source) => {
      if (source === 'subscription') return t('bonusPool.ticketSourceSubscription');
      if (source === 'invite') return t('bonusPool.ticketSourceInvite');
      return source || '-';
    },
    [t]
  );

  const winnersByTier = useMemo(
    () =>
      roundDetail?.winners || {
        first: [],
        second: [],
        third: [],
      },
    [roundDetail]
  );

  const renderWinners = useCallback(
    (tierKey, title, tone) => {
      const items = winnersByTier[tierKey] || [];
      return (
        <div className="rounded-xl border border-glass-border bg-surface/60 p-3">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-slate-500">
            <span>{title}</span>
            <span className={`rounded-full border px-2 py-0.5 text-[11px] ${tone}`}>
              {items.length}
            </span>
          </div>
          {items.length === 0 ? (
            <div className="mt-3 text-xs text-slate-400">{t('bonusPool.winnerEmpty')}</div>
          ) : (
            <div className="mt-3 space-y-2">
              {items.map((winner, idx) => (
                <div
                  key={`${tierKey}-${winner.ticketCode || idx}`}
                  className={`flex items-center justify-between rounded-lg border border-glass-border/60 bg-surface/70 px-3 py-2 text-sm text-slate-200 ${winner.isMe ? 'border-amber-300/70 bg-amber-300/10 shadow-[0_0_18px_rgba(245,158,11,0.35)]' : ''}`}
                >
                  <div className="flex flex-col">
                    <span className="flex items-center gap-2 text-sm font-medium text-white">
                      {winner.displayName || '-'}
                      {winner.isMe && (
                        <span className="rounded-full border border-amber-300/60 bg-amber-400/20 px-2 py-0.5 text-[10px] text-amber-100">
                          {t('bonusPool.winnerMeBadge')}
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-slate-400">
                      {t('bonusPool.ticketCodeLabel')}: {winner.ticketCode || '-'}
                    </span>
                  </div>
                  <div className="text-sm font-semibold text-slate-100">
                    {formatAmount(winner.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    },
    [winnersByTier, t]
  );

  const statusTone = useCallback((status) => {
    if (status === 'ongoing') {
      return 'border-accent-warm/50 bg-accent-warm/20 text-amber-100';
    }
    if (status === 'pending_draw') {
      return 'border-accent/50 bg-accent/20 text-accent-soft';
    }
    if (status === 'ended') {
      return 'border-slate-500/30 bg-slate-500/10 text-slate-400';
    }
    return 'border-glass-border bg-surface/50 text-slate-400';
  }, []);

  const scrollRounds = (direction) => {
    const container = roundsScrollerRef.current;
    if (!container) return;
    const card = container.querySelector('button');
    const step = card ? card.offsetWidth + 12 : 320;
    container.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  const copyToClipboard = useCallback(async (text) => {
    if (!text) {
      return false;
    }
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        return ok;
      } catch {
        return false;
      }
    }
  }, []);

  const handleClaimFetch = useCallback(async () => {
    if (!myPrize?.prizeId) {
      return null;
    }
    setClaiming(true);
    setClaimError('');
    const result = await bonusPoolApi.claimPrize(myPrize.prizeId);
    const responseCode = result.data?.code;
    const isSuccess =
      responseCode === 'SUCCESS' || responseCode === 0 || responseCode === '0';
    if (result.ok && result.data && isSuccess) {
      setClaimResult(result.data.data);
      setClaiming(false);
      return result.data.data;
    }
    setClaimError(result.data?.msg || t('common.error'));
    setClaiming(false);
    return null;
  }, [myPrize?.prizeId, t]);

  useEffect(() => {
    if (!claimModalOpen || !myPrize?.prizeId) {
      return;
    }
    handleClaimFetch();
  }, [claimModalOpen, myPrize?.prizeId, handleClaimFetch]);

  useEffect(() => {
    if (!claimModalOpen || !selectedRoundId) {
      return;
    }
    let cancelled = false;
    const interval = setInterval(async () => {
      const detail = await fetchRoundDetail(selectedRoundId, true);
      if (cancelled || !detail) {
        return;
      }
      const latestPrize = detail.myPrize;
      if (
        latestPrize?.claimStatus === 'pending_payout' &&
        claimResult?.inviteRequired
      ) {
        await handleClaimFetch();
      }
    }, 8000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    claimModalOpen,
    selectedRoundId,
    fetchRoundDetail,
    claimResult?.inviteRequired,
    handleClaimFetch,
  ]);

  return (
    <div className="min-h-screen bg-surface text-slate-100">
      {fireworksActive && (
        <div className="pointer-events-none fixed inset-0 z-40">
          <style>{`
            @keyframes firework-pop {
              0% { transform: translate(-50%, -50%) scale(0.2); opacity: 0; }
              20% { opacity: 1; }
              70% { opacity: 0.9; }
              100% { transform: translate(-50%, -50%) scale(1.2); opacity: 0; }
            }
          `}</style>
          {[
            { left: '18%', top: '18%', size: 220, color: '#fbbf24', delay: '0s' },
            { left: '70%', top: '22%', size: 200, color: '#22d3ee', delay: '0.3s' },
            { left: '42%', top: '35%', size: 260, color: '#f472b6', delay: '0.6s' },
            { left: '25%', top: '55%', size: 240, color: '#a3e635', delay: '0.9s' },
            { left: '75%', top: '60%', size: 220, color: '#facc15', delay: '1.1s' },
          ].map((burst, index) => (
            <div
              key={`burst-${index}`}
              className="absolute rounded-full opacity-0 mix-blend-screen"
              style={{
                left: burst.left,
                top: burst.top,
                width: `${burst.size}px`,
                height: `${burst.size}px`,
                background: `radial-gradient(circle, ${burst.color} 0%, rgba(255,255,255,0) 70%)`,
                animation: `firework-pop 1.8s ease-out ${burst.delay} forwards`,
              }}
            />
          ))}
        </div>
      )}
      {claimModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-xl rounded-3xl border border-glass-border bg-surface/95 p-6 text-slate-100 shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-400">
                  {t('bonusPool.claimModalTitle')}
                </div>
                <div className="mt-2 text-2xl font-semibold text-white">
                  {t('bonusPool.claimModalHeadline')}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setClaimModalOpen(false)}
                className="rounded-full border border-glass-border bg-surface/70 p-2 text-slate-300 hover:bg-surface-highlight"
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>

            {myPrize && (
              <div className="mt-4 rounded-2xl border border-amber-300/30 bg-amber-400/10 p-4">
                <div className="text-sm text-amber-100/80">
                  {t('bonusPool.claimYourPrize')}
                </div>
                <div className="mt-2 text-xl font-semibold text-amber-100">
                  {t('bonusPool.claimCongrats', {
                    tier: t(
                      `bonusPool.winnerTier${
                        myPrize.tier === 'first'
                          ? 'First'
                          : myPrize.tier === 'second'
                            ? 'Second'
                            : 'Third'
                      }`
                    ),
                    amount: formatAmount(myPrize.amount),
                  })}
                </div>
              </div>
            )}

            <div className="mt-4 rounded-2xl border border-glass-border bg-surface/70 p-4 text-sm text-slate-200">
              {claiming && <div>{t('bonusPool.claimChecking')}</div>}
              {!claiming && claimResult?.inviteRequired && (
                <div className="space-y-3">
                  <div>{t('bonusPool.claimInviteHint')}</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        const ok = await copyToClipboard(claimResult.inviteLink);
                        setInviteCopied(ok);
                        if (ok) {
                          setTimeout(() => setInviteCopied(false), 2000);
                        }
                      }}
                      className="inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-amber-400/20 px-3 py-1 text-[11px] text-amber-100 transition hover:bg-amber-400/30"
                    >
                      <FiCopy className="h-3.5 w-3.5" />
                      {inviteCopied ? t('bonusPool.copied') : t('bonusPool.copyLink')}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = await copyToClipboard(claimResult.inviteLink);
                      setInviteCopied(ok);
                      if (ok) {
                        setTimeout(() => setInviteCopied(false), 2000);
                      }
                    }}
                    className="w-full rounded-lg border border-amber-300/30 bg-surface/50 px-3 py-2 text-left text-amber-100 hover:bg-surface/70"
                  >
                    {claimResult.inviteLink}
                  </button>
                </div>
              )}
              {!claiming && claimResult && !claimResult.inviteRequired && (
                <div className="space-y-2">
                  <div>{t('bonusPool.claimPendingPayout')}</div>
                  {claimResult?.walletAddress && (
                    <div className="text-xs text-slate-400">
                      {t('bonusPool.claimWalletAddress')}: {claimResult.walletAddress}
                    </div>
                  )}
                </div>
              )}
              {!claiming && !claimResult && !claimError && (
                <div>{t('bonusPool.claimChecking')}</div>
              )}
              {claimError && <div className="mt-2 text-xs text-red-200">{claimError}</div>}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setClaimModalOpen(false)}
                className="rounded-xl border border-glass-border bg-surface/70 px-4 py-2 text-sm text-slate-200 hover:bg-surface-highlight"
              >
                {t('bonusPool.claimClose')}
              </button>
              {claimResult?.walletAddress && !claimResult?.inviteRequired && (
                <button
                  type="button"
                  onClick={() => setExportKeyModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-xl border border-glass-border bg-surface/70 px-4 py-2 text-sm text-slate-200 hover:bg-surface-highlight"
                >
                  <FiKey className="h-4 w-4" />
                  {t('bonusPool.exportPrivateKey')}
                </button>
              )}
              {claimResult?.inviteRequired && (
                <div className="inline-flex items-center gap-2 rounded-xl border border-amber-300/30 bg-amber-400/10 px-4 py-2 text-sm text-amber-100">
                  <FiRefreshCw className="h-4 w-4 animate-spin" />
                  {t('bonusPool.claimAutoRefreshing')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {exportKeyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-3xl border border-glass-border bg-surface/95 p-6 text-slate-100 shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-400">
                  {t('bonusPool.exportPrivateKey')}
                </div>
                <div className="mt-2 text-lg font-semibold text-white">
                  <FiKey className="mr-2 inline h-5 w-5" />
                  {t('bonusPool.privateKeyLabel')}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setExportKeyModalOpen(false);
                  setExportedPrivateKey(null);
                  setExportKeyError('');
                }}
                className="rounded-full border border-glass-border bg-surface/70 p-2 text-slate-300 hover:bg-surface-highlight"
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>

            {!exportedPrivateKey && !exportKeyLoading && !exportKeyError && (
              <div className="mt-4 space-y-4">
                <div className="rounded-xl border border-amber-300/30 bg-amber-400/10 p-3 text-sm text-amber-100">
                  {t('bonusPool.privateKeyWarning')}
                </div>
                <div className="text-sm text-slate-300">
                  {t('bonusPool.exportPrivateKeyConfirm')}
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setExportKeyModalOpen(false);
                    }}
                    className="flex-1 rounded-xl border border-glass-border bg-surface/70 px-4 py-2 text-sm text-slate-200 hover:bg-surface-highlight"
                  >
                    {t('bonusPool.claimClose')}
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      setExportKeyLoading(true);
                      setExportKeyError('');
                      const result = await bonusPoolApi.exportPrivateKey();
                      const responseCode = result.data?.code;
                      const isSuccess =
                        responseCode === 'SUCCESS' || responseCode === 0 || responseCode === '0';
                      if (result.ok && result.data && isSuccess && result.data.data?.privateKey) {
                        setExportedPrivateKey(result.data.data.privateKey);
                      } else {
                        setExportKeyError(result.data?.msg || t('bonusPool.exportPrivateKeyError'));
                      }
                      setExportKeyLoading(false);
                    }}
                    className="flex-1 rounded-xl border border-amber-300/40 bg-amber-400/20 px-4 py-2 text-sm text-amber-100 hover:bg-amber-400/30"
                  >
                    {t('bonusPool.exportPrivateKey')}
                  </button>
                </div>
              </div>
            )}

            {exportKeyLoading && (
              <div className="mt-4 text-sm text-slate-300">{t('common.loading')}</div>
            )}

            {exportKeyError && (
              <div className="mt-4 text-sm text-red-200">{exportKeyError}</div>
            )}

            {exportedPrivateKey && (
              <div className="mt-4 space-y-4">
                <div className="rounded-xl border border-amber-300/30 bg-amber-400/10 p-3 text-sm text-amber-100">
                  {t('bonusPool.privateKeyWarning')}
                </div>
                <div className="rounded-xl border border-glass-border bg-surface/70 p-3">
                  <div className="break-all font-mono text-xs text-slate-200">
                    {exportedPrivateKey}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await copyToClipboard(exportedPrivateKey);
                    setPrivateKeyCopied(ok);
                    if (ok) {
                      setTimeout(() => setPrivateKeyCopied(false), 2000);
                    }
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-amber-300/40 bg-amber-400/20 px-4 py-2 text-sm text-amber-100 hover:bg-amber-400/30"
                >
                  <FiCopy className="h-4 w-4" />
                  {privateKeyCopied ? t('bonusPool.privateKeyCopied') : t('bonusPool.copyPrivateKey')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8 md:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm uppercase tracking-[0.2em] text-slate-500">
              {t('bonusPool.roundsSubtitle')}
            </div>
            <div className="mt-2 text-2xl font-semibold text-white">
              {t('bonusPool.roundsTitle')}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadRounds}
              className="inline-flex items-center gap-2 rounded-xl border border-glass-border bg-surface/60 px-4 py-2 text-sm text-slate-200 transition-colors hover:bg-surface-highlight"
            >
              <FiRefreshCw className="h-4 w-4" />
              {t('bonusPool.refresh')}
            </button>
            <button
              onClick={() => navigate('/bonus-pool')}
              className="inline-flex items-center gap-2 rounded-xl border border-glass-border bg-surface/60 px-4 py-2 text-sm text-slate-200 transition-colors hover:bg-surface-highlight"
            >
              <FiArrowLeft className="h-4 w-4" />
              {t('bonusPool.backToLanding')}
            </button>
          </div>
        </div>

        {!authLoading && !isAuthenticated && (
          <div className="rounded-2xl border border-glass-border bg-surface/70 p-4 text-sm text-slate-300">
            {t('bonusPool.loginHint')}
          </div>
        )}

        {rounds.length === 0 ? (
          <div className="glass-card p-8 text-center text-sm text-slate-400">
            {emptyState}
          </div>
        ) : (
          <div className="grid gap-4">
            <div className="glass-card overflow-x-hidden p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                    {t('bonusPool.roundListTitle')}
                  </div>
                </div>
              </div>
              <div className="relative mt-4">
                <button
                  type="button"
                  onClick={() => scrollRounds(-1)}
                  className="absolute left-0 top-1/2 z-20 -translate-y-1/2 rounded-full border border-glass-border bg-surface/80 p-2 text-slate-200 shadow-lg hover:bg-surface-highlight"
                >
                  <FiChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollRounds(1)}
                  className="absolute right-0 top-1/2 z-20 -translate-y-1/2 rounded-full border border-glass-border bg-surface/80 p-2 text-slate-200 shadow-lg hover:bg-surface-highlight"
                >
                  <FiChevronRight className="h-4 w-4" />
                </button>
                <div
                  ref={roundsScrollerRef}
                  className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 pl-10 pr-10 scrollbar-thin"
                >
                  {rounds.map((round) => {
                    const isActive = round.id === selectedRoundId;
                    const isOngoing = round.status === 'ongoing';
                    const isPending = round.status === 'pending_draw';
                    const activeTone = isActive
                      ? 'ring-1 ring-white/25 shadow-[0_16px_32px_rgba(10,18,28,0.5)]'
                      : 'hover:shadow-[0_12px_26px_rgba(10,18,28,0.35)]';
                    const cardTone = isActive
                      ? isOngoing
                        ? 'bg-gradient-to-br from-amber-400/30 via-amber-300/15 to-surface/60 border-amber-300/70'
                        : isPending
                          ? 'bg-gradient-to-br from-accent/30 via-accent/10 to-surface/60 border-accent/60'
                          : 'bg-gradient-to-br from-slate-500/20 via-slate-500/10 to-surface/60 border-slate-400/40'
                      : isOngoing
                        ? 'bg-surface/50 border-amber-400/60'
                        : isPending
                          ? 'bg-surface/50 border-accent/50'
                          : 'bg-surface/50 border-slate-500/30';
                    const ongoingTone = isOngoing
                      ? 'shadow-[0_0_30px_rgba(196,154,92,0.4)]'
                      : '';
                    const poolAmountTone = isOngoing
                      ? 'text-amber-100 bg-amber-400/15 border-amber-300/40 shadow-[0_0_18px_rgba(196,154,92,0.45)]'
                      : 'text-white/90';
                    const statusBar =
                      isOngoing
                        ? 'bg-accent-warm'
                        : isPending
                          ? 'bg-accent'
                          : 'bg-slate-500/70';
                    return (
                      <button
                        key={round.id}
                        onClick={() => setSelectedRoundId(round.id)}
                        className={`relative snap-start min-w-[260px] md:min-w-[340px] overflow-hidden rounded-2xl border px-4 py-4 text-left transition-all ${cardTone} ${activeTone} ${ongoingTone}`}
                      >
                        <span className={`absolute left-0 top-0 h-1.5 w-full ${statusBar}`} />
                        <div className="pointer-events-none absolute inset-0 opacity-40">
                          <div className="h-full w-full bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.18),transparent_55%),radial-gradient(circle_at_80%_30%,rgba(255,255,255,0.08),transparent_50%)]" />
                        </div>
                        <div className="relative z-10">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-xs uppercase tracking-[0.2em] text-slate-200/80">
                              {round.id}
                            </div>
                            <span
                              className={`rounded-full border px-2 py-1 text-[11px] ${statusTone(
                                round.status
                              )}`}
                            >
                              {statusLabel(round.status)}
                            </span>
                          </div>
                          <div className="mt-3 text-lg font-semibold text-white">
                            {formatDate(round.startTime)} - {formatDate(round.endTime)}
                          </div>
                          <div className="mt-1 text-xs text-slate-200/80">
                            {formatTime(round.startTime)}
                          </div>
                          {isOngoing && (
                            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-amber-400/10 px-2.5 py-1 text-xs text-amber-100">
                              <FiClock className="h-3.5 w-3.5" />
                              {t('bonusPool.countdown')} {formatCountdown(round.endTime, now)}
                            </div>
                          )}
                          <div
                            className={`mt-3 inline-flex items-center gap-2 rounded-full border border-transparent px-2.5 py-1 text-sm ${poolAmountTone}`}
                          >
                            <FiLayers className="h-4 w-4 text-white/80" />
                            <span className="text-white/60">{t('bonusPool.poolAmount')}</span>
                            <span className="text-white">{formatAmount(round.poolAmount)}</span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {!selectedRound ? (
              <div className="glass-card p-8 text-center text-sm text-slate-400">
                {t('bonusPool.selectRound')}
              </div>
            ) : (
              <div className="glass-card p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400">
                      {selectedRound.id}
                    </div>
                    <div className="mt-2 text-lg font-semibold text-white">
                      {t('bonusPool.currentRoundTitle')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 rounded-full border border-glass-border bg-surface/70 px-3 py-1 text-xs text-slate-300">
                    <FiLayers className="h-3.5 w-3.5 text-accent-soft" />
                    {t('bonusPool.poolAmount')}: {formatAmount(selectedRound.poolAmount)}
                  </div>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-3">
                  <div className="rounded-xl border border-glass-border bg-surface/60 p-3 text-xs text-slate-300">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">
                      {t('bonusPool.startTime')}
                    </div>
                    <div className="mt-1 text-sm text-white">
                      {formatTime(selectedRound.startTime)}
                    </div>
                  </div>
                  <div className="rounded-xl border border-glass-border bg-surface/60 p-3 text-xs text-slate-300">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">
                      {t('bonusPool.endTime')}
                    </div>
                    <div className="mt-1 text-sm text-white">
                      {formatTime(selectedRound.endTime)}
                    </div>
                  </div>
                  {selectedRound.status === 'ongoing' ? (
                    <div className="rounded-xl border border-amber-300/40 bg-amber-400/10 p-3 text-xs text-amber-100">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-amber-200/70">
                        {t('bonusPool.countdown')}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-amber-100">
                        {countdownText || '-'}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-glass-border bg-surface/60 p-3 text-xs text-slate-300">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">
                        {t('bonusPool.drawTime')}
                      </div>
                      <div className="mt-1 text-sm text-white">
                        {formatTime(selectedRound.drawTime)}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-300">
                  <div className="flex items-center gap-2">
                    <FiUsers className="h-4 w-4 text-accent-soft" />
                    {t('bonusPool.subscribers')}: {participantCount}
                  </div>
                  <div className="flex items-center gap-2">
                    <FiClock className="h-4 w-4 text-accent-warm" />
                    {t('bonusPool.myTickets')}:{' '}
                    {(() => {
                      const tickets = ticketMap[selectedRound.id] || [];
                      return tickets.length > 0
                        ? tickets.length
                        : selectedRound.myTicketCount ?? '-';
                    })()}
                  </div>
                </div>

                <div className="mt-5 border-t border-glass-border/70 pt-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 text-xs uppercase tracking-[0.2em] text-slate-500">
                    <span>{t('bonusPool.winnersTitle')}</span>
                  </div>
                  {detailLoading ? (
                    <div className="mt-3 text-xs text-slate-400">{t('common.loading')}</div>
                  ) : (
                    <div className="mt-3 grid gap-3 md:grid-cols-3">
                      {renderWinners('first', t('bonusPool.winnerTierFirst'), 'border-amber-300/50 text-amber-100')}
                      {renderWinners('second', t('bonusPool.winnerTierSecond'), 'border-accent/50 text-accent-soft')}
                      {renderWinners('third', t('bonusPool.winnerTierThird'), 'border-slate-400/40 text-slate-300')}
                    </div>
                  )}
                </div>

                {isAuthenticated && myPrize && (
                  <div className="mt-5 rounded-2xl border border-amber-300/40 bg-amber-400/10 p-4 text-sm text-amber-100">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-amber-200/70">
                          {t('bonusPool.claimTitle')}
                        </div>
                        <div className="mt-2 text-base font-semibold">
                          {t('bonusPool.claimCongrats', {
                            tier: t(`bonusPool.winnerTier${myPrize.tier === 'first' ? 'First' : myPrize.tier === 'second' ? 'Second' : 'Third'}`),
                            amount: formatAmount(myPrize.amount),
                          })}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setClaimModalOpen(true)}
                        className="rounded-xl bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 shadow-[0_10px_22px_rgba(245,158,11,0.35)] transition hover:bg-amber-300"
                      >
                        {t('bonusPool.claimOpen')}
                      </button>
                    </div>
                  </div>
                )}

                {isAuthenticated && (
                  <div className="mt-5 border-t border-glass-border/70 pt-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 text-xs uppercase tracking-[0.2em] text-slate-500">
                      <span>{t('bonusPool.ticketCodesTitle')}</span>
                      <span className="text-[11px] normal-case text-slate-500">
                        {t('bonusPool.myTickets')}:{' '}
                        {(() => {
                          const tickets = ticketMap[selectedRound.id] || [];
                          return tickets.length > 0
                            ? tickets.length
                            : selectedRound.myTicketCount ?? '-';
                        })()}
                      </span>
                    </div>
                    {(() => {
                      const tickets = ticketMap[selectedRound.id] || [];
                      if (ticketLoading && tickets.length === 0) {
                        return (
                          <div className="mt-3 text-xs text-slate-400">
                            {t('common.loading')}
                          </div>
                        );
                      }
                      if (tickets.length === 0) {
                        return (
                          <div className="mt-3 text-xs text-slate-400">
                            {t('bonusPool.noTickets')}
                          </div>
                        );
                      }
                      return (
                        <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                          {tickets.map((ticket) => {
                            const isInvite = ticket.source === 'invite';
                            const sourceTone = isInvite
                              ? 'bg-emerald-400/30 text-emerald-200'
                              : 'bg-amber-400/40 text-amber-100';
                            const ticketGlow = isInvite
                              ? 'shadow-[0_0_24px_rgba(52,211,153,0.35)]'
                              : 'shadow-[0_0_28px_rgba(245,158,11,0.45)]';
                            const ticketTone = isInvite
                              ? 'from-emerald-400/30 border-emerald-300/50'
                              : 'from-amber-400/35 border-amber-300/60';
                            return (
                              <div
                                key={ticket.ticketCode}
                                className={`relative flex items-center gap-3 overflow-hidden rounded-2xl border border-dashed bg-gradient-to-r ${ticketTone} via-surface/70 to-surface/50 px-4 py-3 text-sm text-slate-100 shadow-[0_14px_30px_rgba(40,50,70,0.42)] ${ticketGlow}`}
                              >
                                <span className="absolute left-0 top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-surface" />
                                <span className="absolute right-0 top-1/2 h-3.5 w-3.5 translate-x-1/2 -translate-y-1/2 rounded-full bg-surface" />
                                <div className={`flex h-9 w-9 items-center justify-center rounded-full ${sourceTone}`}>
                                  <FiCreditCard className="h-4 w-4" />
                                </div>
                                <div className="flex min-w-0 flex-1 flex-col">
                                  <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                    {t('bonusPool.ticketCodeLabel')}
                                  </span>
                                  <span className="truncate text-sm font-semibold text-white">
                                    {ticket.ticketCode}
                                  </span>
                                </div>
                                {ticket.source && (
                                  <span className="rounded-full border border-glass-border/70 bg-surface-highlight/40 px-2.5 py-1 text-[11px] text-slate-400">
                                    {t('bonusPool.ticketSourceLabel')} {ticketSourceLabel(ticket.source)}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
